import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { AppointmentDialog } from "./AppointmentDialog";
import { QuickAppointmentDialog } from "./QuickAppointmentDialog";
import { useToast } from "@/hooks/use-toast";

interface Appointment {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  status: string;
  appointment_type: string;
  location: string | null;
  crm_contacts: { name: string; phone_number: string } | null;
  profiles: { full_name: string } | null;
}

export function AppointmentList() {
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickDialogOpen, setQuickDialogOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>();

  useEffect(() => {
    fetchAppointments();

    const channel = supabase
      .channel('appointments-list-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointments' },
        () => {
          fetchAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    let filtered = appointments;

    if (statusFilter !== "all") {
      filtered = filtered.filter((apt) => apt.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (apt) =>
          apt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          apt.crm_contacts?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAppointments(filtered);
  }, [appointments, statusFilter, searchTerm]);

  const fetchAppointments = async () => {
    const { data } = await supabase
      .from("appointments")
      .select("*, crm_contacts(name, phone_number), profiles!appointments_assigned_agent_id_fkey(full_name)")
      .order("start_time", { ascending: false });

    if (data) setAppointments(data as Appointment[]);
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    const { error } = await supabase
      .from("appointments")
      .update({ status: newStatus })
      .eq("id", id);

    if (error) {
      toast({ title: "Error updating status", variant: "destructive" });
    } else {
      toast({ title: "Status updated" });
      fetchAppointments();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
      case "confirmed":
        return "bg-green-500/10 text-green-700 dark:text-green-300";
      case "cancelled":
        return "bg-red-500/10 text-red-700 dark:text-red-300";
      case "completed":
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300";
      default:
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Appointments</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setQuickDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Quick Appointment
          </Button>
          <Button onClick={() => { setSelectedAppointmentId(undefined); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search appointments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="no_show">No Show</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAppointments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No appointments found
                </TableCell>
              </TableRow>
            ) : (
              filteredAppointments.map((apt) => (
                <TableRow key={apt.id} className="cursor-pointer hover:bg-muted/50">
                  <TableCell className="font-medium">{apt.title}</TableCell>
                  <TableCell>
                    {apt.crm_contacts?.name || "N/A"}
                    {apt.crm_contacts?.phone_number && (
                      <div className="text-xs text-muted-foreground">{apt.crm_contacts.phone_number}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>{format(parseISO(apt.start_time), "MMM d, yyyy")}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(parseISO(apt.start_time), "HH:mm")} - {format(parseISO(apt.end_time), "HH:mm")}
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{apt.appointment_type.replace("_", " ")}</TableCell>
                  <TableCell>{apt.profiles?.full_name || "Unassigned"}</TableCell>
                  <TableCell>{apt.location || "-"}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(apt.status)}>{apt.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAppointmentId(apt.id);
                          setDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Select
                        value={apt.status}
                        onValueChange={(value) => handleStatusChange(apt.id, value)}
                      >
                        <SelectTrigger className="w-[120px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="no_show">No Show</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <AppointmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        appointmentId={selectedAppointmentId}
        onSuccess={fetchAppointments}
      />

      <QuickAppointmentDialog
        open={quickDialogOpen}
        onOpenChange={setQuickDialogOpen}
        onSuccess={fetchAppointments}
      />
    </div>
  );
}
